import React from "react"
import PropTypes from "prop-types"
import { Flex, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import styles from "./index.module.css"

/**
 * "Leslie Alexander (Capitan) · Unavailable ⓘ" — an assigned resource a blackout has taken out
 * from under the booking it is still attached to.
 *
 * The chip stays removable rather than disappearing: the assignment is real until a guide
 * replaces or drops it, and a chip that vanished would leave the booking looking staffed. The
 * info icon carries the reason, which is the only place the guide can read *why* without leaving
 * the page. The date sits above it, so the guide can tell a one-day absence from a long one.
 */
const UnavailableChipLabel = ({ label, reason, date }) => {
  const { t } = useTranslation()
  const unavailableText = t("booking.reservation_detail.unavailable", { defaultValue: "Unavailable" })
  const tooltipTitle =
    date || reason ? (
      <Flex vertical>
        {date && <span>{date}</span>}
        {reason && <span>{reason}</span>}
      </Flex>
    ) : (
      ""
    )

  return (
    <span className={styles.wrapper}>
      <span className={styles.label}>{label}</span>
      <span className={styles.state}>{`· ${unavailableText}`}</span>
      {/* antd renders no tooltip for an empty title, so a conflict with neither simply has none. */}
      <Tooltip title={tooltipTitle}>
        <span className={styles.info}>
          <Icon name="Info" width={16} height={16} color="var(--color-primary)" />
        </span>
      </Tooltip>
    </span>
  )
}

UnavailableChipLabel.propTypes = {
  label: PropTypes.string.isRequired,
  /** The blackout's reason, when it gave one. */
  reason: PropTypes.string,
  /** When the resource is unavailable, already formatted ("Oct 7, 2026"). */
  date: PropTypes.string
}

export default UnavailableChipLabel
